import AddIcon from '@mui/icons-material/Add';
import CachedIcon from '@mui/icons-material/Cached';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PublishIcon from '@mui/icons-material/Publish';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { MouseEvent, useCallback, useRef, useState } from 'react';
import { SourcingList, SourcingSavedSearch } from '../../schemas/SourcingList.schema';
import { ISourcingTypeaheadResult } from '../../services/queries/SourcingCompanyQueries';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { SourcingEntitySearch } from './components/SourcingEntitySearch';

interface ListActionsProps {
  readonly entityType: SourcingList['entityType'];
  readonly onDownloadCsv: () => void;
  readonly onMenuButtonClick: (e: MouseEvent<HTMLButtonElement>) => void;
  readonly onEnrichNow: () => void;
  readonly isEnrichLoading: boolean;
  readonly onBulkUpload: () => void;
  readonly isBulkUploadLoading: boolean;
  readonly onAddCompany: (result: ISourcingTypeaheadResult) => void;
  readonly isAddingCompany: boolean;
}

function ListActions({
  entityType,
  onDownloadCsv,
  onMenuButtonClick,
  onEnrichNow,
  isEnrichLoading,
  onBulkUpload,
  isBulkUploadLoading,
  onAddCompany,
  isAddingCompany,
}: ListActionsProps) {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const handleCloseSearch = useCallback(() => setIsSearchActive(false), []);

  const handleSelect = useCallback(
    (result: ISourcingTypeaheadResult) => {
      setIsSearchActive(false);
      onAddCompany(result);
    },
    [onAddCompany]
  );

  useOutsideClick(searchRef, handleCloseSearch);

  return (
    <>
      <IconButton size='small' color='secondary' onClick={onDownloadCsv}>
        <DownloadIcon />
      </IconButton>

      <Button
        variant='outlined'
        color='secondary'
        size='small'
        startIcon={isBulkUploadLoading ? <CircularProgress size={16} color='inherit' /> : <PublishIcon />}
        onClick={onBulkUpload}
        disabled={isBulkUploadLoading}
      >
        Bulk Upload
      </Button>

      {entityType === 'person' ? (
        <Tooltip title='Coming soon'>
          <span>
            <Button variant='outlined' color='secondary' size='small' startIcon={<AddIcon />} disabled>
              Add Person
            </Button>
          </span>
        </Tooltip>
      ) : isSearchActive ? (
        <div ref={searchRef} style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              backgroundColor: 'white',
            }}
          >
            <SourcingEntitySearch onSelect={handleSelect} />
          </div>
        </div>
      ) : (
        <Button
          variant='outlined'
          color='secondary'
          size='small'
          startIcon={isAddingCompany ? <CircularProgress size={16} color='inherit' /> : <AddIcon />}
          onClick={() => setIsSearchActive(true)}
          disabled={isAddingCompany}
        >
          Add Company
        </Button>
      )}

      <Button
        variant='contained'
        color='secondary'
        size='small'
        startIcon={isEnrichLoading ? <CircularProgress size={16} color='inherit' /> : <ReadMoreIcon />}
        onClick={onEnrichNow}
        disabled={isEnrichLoading}
      >
        Enrich Now
      </Button>

      <IconButton size='small' aria-label='List options' onClick={onMenuButtonClick}>
        <MoreVertIcon />
      </IconButton>
    </>
  );
}

interface SavedSearchActionsProps {
  readonly onDownloadCsv: () => void;
  readonly onMenuButtonClick: (e: MouseEvent<HTMLButtonElement>) => void;
  readonly onRunNow: () => void;
  readonly isRunLoading: boolean;
}

function SavedSearchActions({
  onDownloadCsv,
  onMenuButtonClick,
  onRunNow,
  isRunLoading,
}: SavedSearchActionsProps) {
  return (
    <>
      <IconButton size='small' color='secondary' onClick={onDownloadCsv}>
        <DownloadIcon />
      </IconButton>

      <Button
        variant='contained'
        color='secondary'
        size='small'
        startIcon={isRunLoading ? <CircularProgress size={16} color='inherit' /> : <CachedIcon />}
        onClick={onRunNow}
        disabled={isRunLoading}
      >
        Run Now
      </Button>

      <IconButton size='small' aria-label='List options' onClick={onMenuButtonClick}>
        <MoreVertIcon />
      </IconButton>
    </>
  );
}

interface HeaderActionsProps {
  readonly listType: SourcingList['listType'] | SourcingSavedSearch['listType'];
  readonly entityType: SourcingList['entityType'];
  readonly onDownloadCsv: () => void;
  readonly onMenuButtonClick: (e: MouseEvent<HTMLButtonElement>) => void;
  readonly onEnrichNow: () => void;
  readonly isEnrichLoading: boolean;
  readonly onRunNow: () => void;
  readonly isRunLoading: boolean;
  readonly onBulkUpload: () => void;
  readonly isBulkUploadLoading: boolean;
  readonly onAddCompany: (result: ISourcingTypeaheadResult) => void;
  readonly isAddingCompany: boolean;
}

export function HeaderActions({
  listType,
  entityType,
  onDownloadCsv,
  onMenuButtonClick,
  onEnrichNow,
  isEnrichLoading,
  onRunNow,
  isRunLoading,
  onBulkUpload,
  isBulkUploadLoading,
  onAddCompany,
  isAddingCompany,
}: HeaderActionsProps) {
  if (listType === 'active') {
    return (
      <SavedSearchActions
        onDownloadCsv={onDownloadCsv}
        onMenuButtonClick={onMenuButtonClick}
        onRunNow={onRunNow}
        isRunLoading={isRunLoading}
      />
    );
  }

  return (
    <ListActions
      entityType={entityType}
      onDownloadCsv={onDownloadCsv}
      onMenuButtonClick={onMenuButtonClick}
      onEnrichNow={onEnrichNow}
      isEnrichLoading={isEnrichLoading}
      onBulkUpload={onBulkUpload}
      isBulkUploadLoading={isBulkUploadLoading}
      onAddCompany={onAddCompany}
      isAddingCompany={isAddingCompany}
    />
  );
}
